import React, { useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Sheet } from "@/pages/Familia";

const TIPOS = [["fallo", "Algo no funciona", "AlertCircle"], ["idea", "Una idea", "Lightbulb"], ["otro", "Otro", "MessageCircle"]];

// Comentarios de la beta: se guardan y llegan por email al responsable.
export const EnviarComentario = () => {
  const [abierto, setAbierto] = useState(false);
  const [tipo, setTipo] = useState("fallo");
  const [mensaje, setMensaje] = useState("");
  const [captura, setCaptura] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hecho, setHecho] = useState(false);
  const fileRef = useRef(null);
  const cerrar = () => { setAbierto(false); setHecho(false); setMensaje(""); setCaptura(null); setTipo("fallo"); };

  const enviar = async () => {
    if (mensaje.trim().length < 3) return toast.error("Escribe tu comentario.");
    const fd = new FormData();
    fd.append("tipo", tipo); fd.append("mensaje", mensaje.trim());
    fd.append("pantalla", document.referrer ? new URL(document.referrer).pathname : window.location.pathname);
    fd.append("dispositivo", navigator.userAgent);
    if (captura) fd.append("captura", captura);
    setBusy(true);
    try { await api.post("/comentarios", fd); setHecho(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo enviar. Inténtalo de nuevo."); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="perfil-enviar-comentario" onClick={() => setAbierto(true)}
        className="w-full min-h-[56px] bg-white border border-[#E5DFD5] rounded-2xl px-5 flex items-center gap-3 text-left shadow-sm hover:bg-[#FAF8F5]">
        <span className="w-10 h-10 rounded-xl bg-[#E8F0EC] flex items-center justify-center"><Icons.MessageSquareText size={20} className="text-[#3A6B56]" /></span>
        <span className="flex-1 font-semibold text-base text-[#1C2421]">Enviar comentario</span>
        <Icons.ChevronRight size={20} className="text-[#C4BDB0]" />
      </button>
      {abierto && (
        <Sheet onClose={cerrar} testid="comentario-sheet">
          {hecho ? (
            <div className="py-10 text-center" data-testid="comentario-gracias">
              <Icons.CheckCircle2 size={56} className="mx-auto text-[#3A6B56]" />
              <p className="font-heading text-2xl font-bold text-[#1C2421] mt-4">¡Gracias! Lo hemos recibido.</p>
              <p className="text-base text-[#6E7A74] mt-1">Tu opinión nos ayuda a mejorar AL DÍA.</p>
              <button data-testid="comentario-cerrar" onClick={cerrar} className="mt-6 w-full min-h-[52px] rounded-2xl bg-[#3A6B56] text-white font-semibold text-lg">Cerrar</button>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">Enviar comentario</h2>
              <p className="text-base text-[#6E7A74] mt-1">Cuéntanos qué ha pasado o qué mejorarías.</p>
              <div className="grid grid-cols-3 gap-2 mt-5" role="radiogroup" aria-label="Tipo de comentario">
                {TIPOS.map(([id, l, ic]) => {
                  const I = Icons[ic]; const sel = tipo === id;
                  return (
                    <button key={id} type="button" role="radio" aria-checked={sel} data-testid={`comentario-tipo-${id}`} onClick={() => setTipo(id)}
                      className={`min-h-[72px] rounded-xl border-2 text-sm font-semibold flex flex-col items-center justify-center gap-1 px-1 ${sel ? "bg-[#3A6B56] border-[#3A6B56] text-white" : "bg-white border-[#E5DFD5] text-[#1C2421]"}`}>
                      <I size={20} /> {l}
                    </button>
                  );
                })}
              </div>
              <textarea data-testid="comentario-mensaje" rows={5} value={mensaje} onChange={(e) => setMensaje(e.target.value)} maxLength={4000}
                placeholder={tipo === "fallo" ? "¿Qué estabas haciendo y qué ha fallado?" : "Escribe aquí tu comentario…"}
                className="mt-4 w-full px-4 py-3 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/15" />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="comentario-captura-input"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f && f.size > 8 * 1024 * 1024) return toast.error("La captura supera 8 MB."); setCaptura(f || null); }} />
              {captura ? (
                <div className="mt-3 flex items-center gap-2 bg-[#E8F0EC] rounded-xl px-3 py-2" data-testid="comentario-captura-ok">
                  <Icons.Image size={18} className="text-[#2E5645]" /><span className="flex-1 text-sm truncate">{captura.name}</span>
                  <button data-testid="comentario-captura-quitar" onClick={() => setCaptura(null)} aria-label="Quitar captura" className="w-10 h-10 flex items-center justify-center"><Icons.X size={18} /></button>
                </div>
              ) : (
                <button data-testid="comentario-captura" onClick={() => fileRef.current?.click()}
                  className="mt-3 w-full min-h-[52px] rounded-xl border border-dashed border-[#C9C1B3] text-[#3A6B56] font-semibold inline-flex items-center justify-center gap-2">
                  <Icons.ImagePlus size={18} /> Adjuntar una captura (opcional)
                </button>
              )}
              <p className="text-sm text-[#6E7A74] mt-2" data-testid="comentario-captura-aviso">No adjuntes capturas con informes médicos ni recetas.</p>
              <button data-testid="comentario-enviar" disabled={busy} onClick={enviar}
                className="mt-5 w-full min-h-[56px] rounded-2xl bg-[#3A6B56] text-white font-bold text-lg disabled:opacity-60">
                {busy ? "Enviando…" : "Enviar comentario"}
              </button>
            </>
          )}
        </Sheet>
      )}
    </>
  );
};
